import {
  Component,
  PLATFORM_ID,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { NodeCalcov, Node } from '@app/models/entity.model';
import { TextComponent } from '@app/components/text/text.component';
import { SplitButtonSectionComponent } from '@app/components/split-button-section/split-button-section.component';
import { TeaserComponent } from '@app/components/teaser/teaser.component';
import { ViewComponent } from '@app/components/view/view.component';
import { TeaserListComponent } from '@app/components/teaser-list/teaser-list.component';
import { ContentSectionComponent } from '@app/components/content-section/content-section.component';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { TextsService } from '@app/services/texts.service';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { TeaserStyles } from '@app/models/global.model';
import { CalcUrlPipe } from '@app/pipes/calc-url.pipe';
import { ViewRow } from '@app/models/view.model';
import { PrerolloutOverlayComponent } from '@app/components/product/prerollout-overlay/prerollout-overlay.component';
import { ExtractUrlPipe } from '@app/pipes/extract-url.pipe';

interface TabItem {
  label: string | undefined;
  title: string | undefined;
  subtitle: string | undefined;
  subtext: string | undefined;
  teaser: Node | undefined;
  overviewTitle: string | undefined;
  viewId: string;
  viewDisplay: string;
}

interface CalcovRow extends ViewRow {
  field_cm_teaser_image: any;
  field_product_cacta?: any;
  field_product_cocta?: any;
  field_profle_cacta?: any;
  field_profle_cacta_auth?: any;
  field_product_cacta_auth?: any;
  field_product_cacta_prer?: any;
  type: string;
  path: { alias: string };
  field_cm_teaser_title: string;
  field_cm_teaser_text: { value: string };
  field_cm_hcalcov: any;
  field_cm_procov_illu: any;
  drupal_internal__nid?: string;
}

@Component({
  selector: 'mobi-node-calcov',
  templateUrl: './node-calcov.component.html',
  styleUrls: ['./node-calcov.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    TextComponent,
    SplitButtonSectionComponent,
    TeaserComponent,
    ViewComponent,
    TeaserListComponent,
    ContentSectionComponent,
    CalcUrlPipe,
    PrerolloutOverlayComponent,
  ],
  providers: [ExtractUrlPipe, CalcUrlPipe],
})
export class NodeCalcovComponent {
  readonly entity = input.required<NodeCalcov>();
  readonly params = input<URLSearchParams>(new URLSearchParams());
  activeItem = linkedSignal<number>(() =>
    this.params().get('page') === 'corporate' ? 1 : 0
  );
  hasSwitchedPage = signal(false);
  #texts = inject(TextsService);
  #document = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);
  readonly teaserStyle = input<TeaserStyles>('card-illustration');
  tabValues = computed<TabItem[]>(() => {
    const entityValue = this.entity();
    if (!entityValue) return [];
    return [
      {
        label: this.entity().overviewPrivateTitle,
        title: this.entity().promotedPrivateTitle,
        subtitle: this.entity().promotedSubsctionPrivateTitle,
        subtext: this.entity().promotedSubsctionPrivateText,
        teaser: this.entity().promotedSubsctionPrivateTeaser,
        overviewTitle: this.entity().overviewPrivateTitle,
        viewId: this.entity().viewRefPrivate?.view?.id || 'calc_products_pr',
        viewDisplay: this.entity().viewRefPrivate?.view?.display || 'default',
      },
      {
        label: this.entity()?.overviewCorporateTitle,
        title: this.entity().promotedCorporateTitle,
        subtitle: this.entity().promotedSubsctionCorporateTitle,
        subtext: this.entity().promotedSubsctionCorporateText,
        teaser: this.entity().promotedSubsctionCorporateTeaser,
        overviewTitle: this.entity().overviewCorporateTitle,
        viewId: this.entity().viewRefCorporate?.view?.id || 'calc_products_co',
        viewDisplay: this.entity().viewRefCorporate?.view?.display || 'default',
      },
    ];
  });
  tabLabels = computed<string[]>(() =>
    this.tabValues().map((item) => item.label as string)
  );

  secondaryCtaLabel = signal('');

  /** Prerollout overlay reference */
  readonly prerolloutOverlay = viewChild<PrerolloutOverlayComponent>('prerolloutOverlay');

  /** Current prerollout configuration for promoted teaser */
  #prerolloutNodeId = signal<string>('');
  #prerolloutUrl = signal<string>('');
  #prerolloutRegularUrl = signal<string>('');
  #prerolloutCtaTarget = signal<string>('');
  #prerolloutTrackingContext = signal<'link' | 'card'>('link');
  #prerolloutTrackingName = signal<string>('');

  prerolloutNodeId = this.#prerolloutNodeId.asReadonly();
  prerolloutUrl = this.#prerolloutUrl.asReadonly();
  prerolloutRegularUrl = this.#prerolloutRegularUrl.asReadonly();
  prerolloutCtaTarget = this.#prerolloutCtaTarget.asReadonly();
  prerolloutTrackingContext = this.#prerolloutTrackingContext.asReadonly();
  prerolloutTrackingName = this.#prerolloutTrackingName.asReadonly();

  #extractUrl = inject(ExtractUrlPipe);
  #calcUrl = inject(CalcUrlPipe);

  constructor() {
    Promise.all([
      this.#texts.getText(
        'teaser_label_show_product',
        'frontend',
        'Mehr über das Produkt erfahren'
      ),
    ])
      .then((texts) => {
        this.secondaryCtaLabel.set(texts[0]);
      })
      .catch((e) => console.log(e));
  }

  ngOnInit() {
    if (this.params().get('page') === 'private') {
      this.updatePageParam(0);
    }
  }

  updatePageParam(index: number) {
    if (!isPlatformBrowser(this.#platformId)) return;

    const url = new URL(this.#document.location.href);
    if (index === 1) {
      url.searchParams.set('page', 'corporate');
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }

  changePageContent(label: string) {
    const index = this.tabLabels().indexOf(label);
    this.hasSwitchedPage.set(true);
    this.activeItem.set(index);
    this.updatePageParam(index);
  }

  /** Check if a teaser has prerollout configured */
  hasPrerollout(teaser: any): boolean {
    return Boolean(teaser?.calculatorCtaPrer?.uri?.path && teaser?.id);
  }

  /** Handle calculator CTA click for promoted teaser with prerollout */
  onPromotedTeaserClick(event: MouseEvent, teaser: any): void {
    const calculatorCta = teaser.calculatorCta;
    const calculatorCtaAuth = teaser.calculatorCtaAuth;
    const regularUrl =
      this.#calcUrl.transform(
        this.#extractUrl.transform(calculatorCta),
        this.#extractUrl.transform(calculatorCtaAuth)
      ) || '';

    this.#prerolloutNodeId.set(teaser.id);
    this.#prerolloutUrl.set(teaser.calculatorCtaPrer?.uri?.path || '');
    this.#prerolloutRegularUrl.set(regularUrl);
    this.#prerolloutCtaTarget.set(calculatorCta?.options?.attributes?.target || '_self');
    this.#prerolloutTrackingContext.set('card');
    this.#prerolloutTrackingName.set(teaser.teaserTitle || teaser.title || '');

    this.prerolloutOverlay()?.openOverlay();
  }

  /** Handle prerollout click from teaser-list */
  onTeaserListPrerolloutClick(event: {
    id: string;
    prerolloutUrl: string;
    regularUrl: string;
    ctaTarget: string;
  }): void {
    this.#prerolloutNodeId.set(event.id);
    this.#prerolloutUrl.set(event.prerolloutUrl);
    this.#prerolloutRegularUrl.set(event.regularUrl);
    this.#prerolloutCtaTarget.set(event.ctaTarget);
    this.#prerolloutTrackingContext.set('card');
    this.#prerolloutTrackingName.set('');

    this.prerolloutOverlay()?.openOverlay();
  }

  formatRows(rows: CalcovRow[]): any[] {
    try {
      return rows.map((row) => {
        const mapped: any = {
          id: row.drupal_internal__nid,
          published: true,
          teaserImage: null,
          teaserTitle: row.field_cm_teaser_title,
          teaserText: row.field_cm_teaser_text?.value,
          bundle: [row.type], // usually an array
          url: {
            title:
              row.field_product_cacta?.title ||
              row.field_product_cocta?.title ||
              row.field_profle_cacta?.title,
            path:
              row.field_product_cacta?.url ||
              row.field_product_cocta?.url ||
              row.field_profle_cacta?.url,
            options: row.field_product_cacta?.options || row.field_product_cocta?.options,
          },
          calculatorCtaAuth: {
            uri: {
              path: row.field_product_cacta_auth?.uri || row.field_profle_cacta_auth?.uri,
            },
          },
          consultationCta: {
            uri: {
              path: row.path.alias,
            },
          },
          procovIllu: null,
          // Prerollout data for teaser-list
          calculatorCtaPrer: row.field_product_cacta_prer
            ? { uri: { path: row.field_product_cacta_prer.uri } }
            : undefined,
          ctaTarget:
            row.field_product_cacta?.options?.attributes?.target ||
            row.field_product_cocta?.options?.attributes?.target ||
            '_self',
        };

        if (row.field_cm_procov_illu) {
          mapped.procovIllu = {
            name: row.field_cm_procov_illu?.name,
            file: {
              entity: { filename: row.field_cm_procov_illu?.field_svg_file?.filename },
            },
          };
        }

        if (row.field_cm_teaser_image?.field_mimg_rhash) {
          mapped.teaserImage = {
            type: 'MediaImage',
            description: row.field_cm_teaser_image?.field_cm_desc,
            image: {
              ...row.field_cm_teaser_image?.field_media_image?.meta,
              entity: {
                filename: row.field_cm_teaser_image?.field_media_image?.filename,
                rokka: {
                  hash: row.field_cm_teaser_image?.field_mimg_rhash,
                },
              },
            },
          };
        }
        return mapped;
      });
    } catch (e) {
      console.error(
        'View is not compatible with the rowFormatter function in node-calcov component',
        e
      );
      return [];
    }
  }
}
